import React, {FC, useEffect, useMemo, useState} from 'react';
import {dispatch} from 'app/store';
import {Button, GoForwardIcon, LoadingSpinner, PaystackIcon} from 'components';
import {FormSelect, FormTextInput} from 'components/Basic/Form/V2';
import Modal from 'components/Basic/Modal';
import {PaystackAccountSchema} from 'definitions/Yup/paystackAccountSchema';
import {selectPaystackSubaccountStatus, selectUserProfile} from 'features/User';
import {
  PaymentService,
  PaystackSubaccountDetails,
  PrescriberProfile,
  SliceStatus,
  TherapistProfile,
} from 'interfaces';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import {yupResolver} from '@hookform/resolvers/yup';

import {
  selectSouthAfricanBankList,
  selectSouthAfricanBankListStatus,
} from '../digitalPracticeSelectors';
import {digitalPracticeActions} from '../digitalPracticeSlice';

import ProcessingFee from './ProcessingFee';
export const PaystackOnboardingLink: FC = () => {
  const {t} = useTranslation();

  const {paystackSubaccountDetails, role, countryOfResidence} = useSelector(
    selectUserProfile,
  ) as TherapistProfile | PrescriberProfile;
  const southAfricanBanksStatus = useSelector(selectSouthAfricanBankListStatus);
  const southAfricanBanks = useSelector(selectSouthAfricanBankList);
  const isLoading = southAfricanBanksStatus === SliceStatus.pending;
  const paystackSubaccountStatus = useSelector(selectPaystackSubaccountStatus);
  const isSubmitting = paystackSubaccountStatus === SliceStatus.pending;

  const [show, setShow] = useState(false);

  const {handleSubmit, control} = useForm({
    defaultValues: {
      bankCode: paystackSubaccountDetails?.bankCode ?? '',
      bankAccountNumber: paystackSubaccountDetails?.bankAccountNumber ?? '',
      subaccountName: paystackSubaccountDetails?.subaccountName ?? '',
    },
    resolver: yupResolver(PaystackAccountSchema),
  });

  useEffect(() => {
    dispatch(digitalPracticeActions.getSouthAfricanBankList({role}));
  }, []);

  useEffect(() => {
    if (paystackSubaccountStatus === SliceStatus.resolved) {
      setShow(false);
    }
  }, [paystackSubaccountStatus]);

  const handlePaystackAccountLink = () => {
    setShow(true);
  };

  const bankOptions = useMemo(() => {
    return southAfricanBanks.map(bank => ({
      value: bank.code.toString(),
      label: bank.name,
    }));
  }, [southAfricanBanks]);

  const onSubmit = (data: PaystackSubaccountDetails) => {
    paystackSubaccountDetails?.subaccountId
      ? dispatch(
          digitalPracticeActions.updatePaystackSubaccount({
            role,
            account: data,
          }),
        )
      : dispatch(
          digitalPracticeActions.createPaystackSubaccount({
            role,
            account: data,
          }),
        );
  };

  return (
    <>
      <button
        className="w-full flex items-center justify-between text-center text-lg  border rounded-lg h-14 px-5 hover:bg-blue-600/10 hover:border-transparent group"
        onClick={handlePaystackAccountLink}
      >
        <div className="flex items-center space-x-3">
          <PaystackIcon classes="w-6 h-auto" />
          <p className="text-left">
            {paystackSubaccountDetails?.bankAccountNumber
              ? t('providerProfileMenuItems.revenue.dp.goToPaystackAccount')
              : t('providerProfileMenuItems.revenue.dp.linkToPaystackAccount')}
          </p>
        </div>
        <GoForwardIcon
          fillColor="#315eff"
          strokeColor="text-blue-600"
          strokeWidth={0.3}
          classes="group-hover:animate-shake"
        />
      </button>

      <ProcessingFee
        countryCode={countryOfResidence?.code}
        paymentService={PaymentService.PAYSTACK}
      />

      <Modal
        messageType="none"
        isOpen={show}
        showCloseIcon
        headerClasses="w-full h-16 px-4 flex justify-end items-center"
        containerClasses="w-full max-h-[90vh] bg-white max-w-[645px] overflow-hidden transform rounded-lg text-left align-middle transition-all shadow-lg rounded-2xl"
        title={t('providerProfileMenuItems.revenue.dp.paystackAccount')}
        titleClasses="text-lg font-semibold"
        footerClasses="sticky bottom-0 bg-white"
        footerHeight="94px"
        floatingTitle={false}
        buttonFn={fromOutside => {
          if (!fromOutside) {
            setShow(false);
          }
        }}
        footer={
          <section className="flex w-full justify-end gap-2 text-center">
            <Button
              type="button"
              borderRadius="full"
              borderColor="blue-600"
              bgColor="white"
              onClick={() => setShow(false)}
              outline
              className="text-blue-600 bg-white min-w-[120px] rounded-xl p-3"
            >
              {t('cancel')}
            </Button>
            <Button
              disabled={isSubmitting}
              type="submit"
              form="booking-form"
              borderColor="transparent"
              className="min-w-[120px] rounded-xl p-3 flex justify-center items-center"
            >
              {isSubmitting ? (
                <LoadingSpinner type="Oval" height={20} />
              ) : paystackSubaccountDetails?.subaccountId ? (
                t('Save')
              ) : (
                t('Update')
              )}
            </Button>
          </section>
        }
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mx-auto px-6 overflow-y-auto"
          id="booking-form"
        >
          <FormSelect
            control={control}
            id="bankCode"
            label={t('providerProfileMenuItems.revenue.dp.bankName')}
            classes="mb-5"
            placeholder={t('providerProfileMenuItems.revenue.dp.chooseBank')}
            detachedSearchBar={true}
            labelClasses="font-light text-xs"
            extractValue={true}
            isLoading={isLoading}
            options={bankOptions}
          />
          <FormTextInput
            control={control}
            id="bankAccountNumber"
            label={t('providerProfileMenuItems.revenue.dp.accountNumber')}
            classes="mb-5"
            inputClasses="rounded-xl p-3.5"
            placeholder={t('providerProfileMenuItems.revenue.dp.accountNumber')}
            labelClasses="font-light text-xs"
          />
          <FormTextInput
            control={control}
            id="subaccountName"
            label={t('providerProfileMenuItems.revenue.dp.nameOfSubaccount')}
            classes="mb-5"
            placeholder={t('providerProfileMenuItems.revenue.dp.eg')}
            labelClasses="font-light text-xs"
            inputClasses="rounded-xl p-3.5"
          />
        </form>
      </Modal>
    </>
  );
};

export default PaystackOnboardingLink;
